import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Plus, Trash2, Users, Eye, EyeOff, RefreshCw, UserCheck, UserX, Search, Clock, ChevronLeft, ChevronRight, Pencil, Shield, Key } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

import { API_URL } from '../utils/api';

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, adrenalin: 0 });
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [adding, setAdding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const pageSize = 50;

  // Edit user state
  const [editUser, setEditUser] = useState(null);
  const [editData, setEditData] = useState({});
  const [editPassword, setEditPassword] = useState('');
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resyncingAll, setResyncingAll] = useState(false);
  const [zohoForms, setZohoForms] = useState(null);

  // Debounced search
  const [searchDebounce, setSearchDebounce] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        search: searchDebounce,
        status: statusFilter,
        source: sourceFilter
      });
      const response = await axios.get(`${API_URL}/api/admin/users?${params}`, { withCredentials: true });
      const data = response.data;
      setUsers(data.users);
      setStats(data.stats);
      setTotalPages(data.total_pages);
      setTotalFiltered(data.total);
    } catch (error) {
      if (error.response?.status === 403) { toast.error('Admin access required'); navigate('/dashboard'); }
      else toast.error('Failed to load users');
    } finally { setLoading(false); }
  }, [navigate, page, searchDebounce, statusFilter, sourceFilter]);

  const fetchSyncLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/sync-logs`, { withCredentials: true });
      setSyncLogs(response.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    fetchUsers();
    fetchSyncLogs();
  }, [user, navigate, fetchUsers, fetchSyncLogs]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [searchDebounce, statusFilter, sourceFilter]);

  const handleSyncEmployees = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/sync-employees`, {}, { withCredentials: true });
      toast.success(response.data.message);
      const pollInterval = setInterval(async () => {
        await fetchSyncLogs();
        await fetchUsers();
        const logs = await axios.get(`${API_URL}/api/admin/sync-logs`, { withCredentials: true });
        const running = logs.data.find(l => l.status === 'running');
        if (!running) {
          clearInterval(pollInterval);
          setSyncing(false);
          toast.success('Employee sync completed!');
        }
      }, 5000);
      setTimeout(() => { clearInterval(pollInterval); setSyncing(false); }, 300000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start sync');
      setSyncing(false);
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const response = await axios.patch(`${API_URL}/api/admin/users/${userId}/toggle-status`, {}, { withCredentials: true });
      toast.success(response.data.message);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: response.data.is_active } : u));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle status');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) { toast.error('Please fill all fields'); return; }
    if (newUser.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/admin/users`, newUser, { withCredentials: true });
      toast.success('User created successfully');
      setShowAddDialog(false);
      setNewUser({ name: '', email: '', password: '' });
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create user'); }
    finally { setAdding(false); }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${deleteUserId}`, { withCredentials: true });
      toast.success('User deleted');
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete user'); }
    finally { setDeleteUserId(null); }
  };

  const openEditDialog = (u) => {
    setEditUser(u);
    setEditData({
      name: u.name || '',
      email: u.email || '',
      department: u.department || '',
      designation: u.designation || '',
      employee_id: u.employee_id || '',
      role: u.role || 'user'
    });
    setEditPassword('');
    setShowEditPassword(false);
  };

  const handleSaveUser = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const payload = { ...editData };
      if (editPassword) payload.password = editPassword;
      await axios.put(`${API_URL}/api/admin/users/${editUser.id}`, payload, { withCredentials: true });
      toast.success('User updated successfully');
      setEditUser(null);
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update user'); }
    finally { setSaving(false); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try { return format(new Date(dateString), 'MMM d, yyyy HH:mm'); } catch { return dateString; }
  };

  const lastSync = syncLogs.find(l => !l.status);

  const handleFetchZohoForms = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/zoho-forms`, { withCredentials: true });
      setZohoForms(response.data);
    } catch (error) {
      toast.error('Failed to fetch Zoho forms');
    }
  };

  const handleResyncAllZoho = async () => {
    setResyncingAll(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/resync-all-zoho`, {}, { withCredentials: true });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start bulk re-sync');
    } finally {
      setResyncingAll(false);
    }
  };

  const handleResyncOneForm = async (formId, title) => {
    try {
      const response = await axios.post(`${API_URL}/api/forms/${formId}/resync-zoho`, {}, { withCredentials: true });
      toast.success(`${title}: ${response.data.message}`);
    } catch (error) {
      toast.error(`${title}: ${error.response?.data?.detail || 'Resync failed'}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="admin-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-slate-600" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />Back
              </Button>
              <div className="border-l border-slate-200 h-6"></div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                <h1 className="text-lg font-semibold font-['Outfit'] text-slate-900">User Management</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={handleSyncEmployees} disabled={syncing} variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50" data-testid="sync-employees-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync from Adrenalin'}
              </Button>
              <Button onClick={() => setShowAddDialog(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-user-btn">
                <Plus className="w-4 h-4 mr-2" />Add User
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card><CardContent className="p-4"><p className="text-2xl font-semibold text-indigo-600">{stats.total}</p><p className="text-xs text-slate-500">Total Users</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-2xl font-semibold text-green-600">{stats.active}</p><p className="text-xs text-slate-500">Active</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-2xl font-semibold text-red-500">{stats.inactive}</p><p className="text-xs text-slate-500">Inactive</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-2xl font-semibold text-slate-700">{stats.adrenalin}</p><p className="text-xs text-slate-500">From Adrenalin</p></CardContent></Card>
          <Card><CardContent className="p-4"><div className="flex items-center gap-1 text-slate-500 mb-1"><Clock className="w-3 h-3" /><span className="text-[10px]">Last Sync</span></div><p className="text-xs font-medium text-slate-700">{lastSync ? formatDate(lastSync.synced_at) : 'Never'}</p>{lastSync && <p className="text-[10px] text-slate-400">+{lastSync.created} new, {lastSync.deactivated} deactivated</p>}</CardContent></Card>
        </div>

        {/* Zoho Sync Section */}
        <Card className="mb-6" data-testid="zoho-sync-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-700">Zoho Sheet Sync</h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleFetchZohoForms} data-testid="view-zoho-forms-btn">
                  View Zoho Forms
                </Button>
                <Button size="sm" onClick={handleResyncAllZoho} disabled={resyncingAll} className="bg-indigo-600 hover:bg-indigo-700" data-testid="resync-all-zoho-btn">
                  <RefreshCw className={`w-3 h-3 mr-1 ${resyncingAll ? 'animate-spin' : ''}`} />
                  {resyncingAll ? 'Starting...' : 'Re-sync All Forms to Zoho'}
                </Button>
              </div>
            </div>
            {zohoForms && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs">Form</TableHead>
                      <TableHead className="text-xs">Owner</TableHead>
                      <TableHead className="text-xs">Zoho Status</TableHead>
                      <TableHead className="text-xs">Responses</TableHead>
                      <TableHead className="text-xs">Worksheet</TableHead>
                      <TableHead className="text-xs">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {zohoForms.map(f => (
                      <TableRow key={f.id}>
                        <TableCell className="text-sm font-medium">{f.title}</TableCell>
                        <TableCell className="text-sm text-slate-600">{f.owner_email}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-1 rounded-full ${f.zoho_connected && f.has_token ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {f.zoho_connected && f.has_token ? 'Connected' : 'Disconnected'}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{f.response_count}</TableCell>
                        <TableCell className="text-xs text-slate-500">{f.worksheet}</TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => handleResyncOneForm(f.id, f.title)} disabled={!f.zoho_connected} data-testid={`resync-form-${f.id}`}>
                            Re-sync
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by name, email, ID, department..." className="pl-9 h-9" data-testid="user-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36 h-9" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-36 h-9" data-testid="source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="adrenalin">Adrenalin</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-400 self-center">{totalFiltered} users</span>
        </div>

        {/* Users Table */}
        <Card data-testid="users-table-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-medium text-slate-700 text-xs">Emp ID</TableHead>
                    <TableHead className="font-medium text-slate-700 text-xs">Name</TableHead>
                    <TableHead className="font-medium text-slate-700 text-xs">Email</TableHead>
                    <TableHead className="font-medium text-slate-700 text-xs">Department</TableHead>
                    <TableHead className="font-medium text-slate-700 text-xs">Designation</TableHead>
                    <TableHead className="font-medium text-slate-700 text-xs">Company</TableHead>
                    <TableHead className="font-medium text-slate-700 text-xs">Manager</TableHead>
                    <TableHead className="font-medium text-slate-700 text-xs">Role</TableHead>
                    <TableHead className="font-medium text-slate-700 text-xs">Status</TableHead>
                    <TableHead className="font-medium text-slate-700 text-xs">Source</TableHead>
                    <TableHead className="font-medium text-slate-700 text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className={u.is_active === false ? 'opacity-50' : ''} data-testid={`user-row-${u.id}`}>
                      <TableCell className="text-xs text-slate-500 font-mono">{u.employee_id || '-'}</TableCell>
                      <TableCell className="font-medium text-slate-900 text-sm">{u.name}</TableCell>
                      <TableCell className="text-slate-700 text-xs">{u.email}</TableCell>
                      <TableCell className="text-slate-600 text-xs">{u.department || '-'}</TableCell>
                      <TableCell className="text-slate-600 text-xs">{u.designation || '-'}</TableCell>
                      <TableCell className="text-slate-600 text-xs">{u.company_name || '-'}</TableCell>
                      <TableCell className="text-slate-600 text-xs">{u.reporting_manager || '-'}</TableCell>
                      <TableCell><span className={`px-2 py-0.5 rounded-full text-[10px] ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'}`}>{u.role || 'user'}</span></TableCell>
                      <TableCell><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${u.is_active === false ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`} data-testid={`user-status-${u.id}`}>{u.is_active === false ? 'Inactive' : 'Active'}</span></TableCell>
                      <TableCell><span className={`text-[10px] ${u.source === 'adrenalin' ? 'text-blue-600' : 'text-slate-400'}`}>{u.source === 'adrenalin' ? 'Adrenalin' : 'Manual'}</span></TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(u)} className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600" title="Edit user" data-testid={`edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>
                          {u.role !== 'admin' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(u.id)} className={`h-7 w-7 p-0 ${u.is_active === false ? 'text-green-500 hover:text-green-700' : 'text-amber-500 hover:text-amber-700'}`} title={u.is_active === false ? 'Activate' : 'Deactivate'} data-testid={`toggle-status-${u.id}`}>{u.is_active === false ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}</Button>
                              <Button variant="ghost" size="sm" onClick={() => setDeleteUserId(u.id)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                <p className="text-xs text-slate-500">Page {page} of {totalPages} ({totalFiltered} total)</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn"><ChevronLeft className="w-4 h-4" /></Button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) { pageNum = i + 1; }
                    else if (page <= 3) { pageNum = i + 1; }
                    else if (page >= totalPages - 2) { pageNum = totalPages - 4 + i; }
                    else { pageNum = page - 2 + i; }
                    return (
                      <Button key={pageNum} variant={page === pageNum ? 'default' : 'outline'} size="sm" onClick={() => setPage(pageNum)} className={`w-8 h-8 p-0 ${page === pageNum ? 'bg-indigo-600' : ''}`}>{pageNum}</Button>
                    );
                  })}
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">Add New User</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2"><Label htmlFor="name">Full Name</Label><Input id="name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="John Doe" data-testid="new-user-name" /></div>
            <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="john@example.com" data-testid="new-user-email" /></div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Min 6 characters" className="pr-10" data-testid="new-user-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddUser} disabled={adding} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-add-user">{adding ? 'Creating...' : 'Create User'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete User</AlertDialogTitle><AlertDialogDescription>Are you sure? This will permanently delete the user.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="max-w-lg" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">Edit User</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={editData.name || ''} onChange={(e) => setEditData({ ...editData, name: e.target.value })} data-testid="edit-user-name" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={editData.email || ''} onChange={(e) => setEditData({ ...editData, email: e.target.value })} data-testid="edit-user-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Input value={editData.department || ''} onChange={(e) => setEditData({ ...editData, department: e.target.value })} data-testid="edit-user-department" />
              </div>
              <div className="space-y-2">
                <Label>Designation</Label>
                <Input value={editData.designation || ''} onChange={(e) => setEditData({ ...editData, designation: e.target.value })} data-testid="edit-user-designation" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Employee ID</Label>
              <Input value={editData.employee_id || ''} onChange={(e) => setEditData({ ...editData, employee_id: e.target.value })} data-testid="edit-user-empid" />
            </div>

            {/* Role Assignment */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Shield className="w-4 h-4 text-indigo-600" />Role</Label>
              <Select value={editData.role || 'user'} onValueChange={(val) => setEditData({ ...editData, role: val })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Set Password */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Key className="w-4 h-4 text-amber-600" />Set New Password</Label>
              <div className="relative">
                <Input type={showEditPassword ? "text" : "password"} value={editPassword} onChange={(e) => setEditPassword(e.target.value)} placeholder="Leave blank to keep current" className="pr-10" data-testid="edit-user-password" />
                <button type="button" onClick={() => setShowEditPassword(!showEditPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">{showEditPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
              <p className="text-xs text-slate-400">Min 6 characters. Leave blank to keep current password.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={handleSaveUser} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-edit-user">{saving ? 'Saving...' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
